import { CSSProperties } from "react";

import classNames from "classnames";
import { isEmpty } from "lodash";

import { useTranslation } from "components/i18n/source";
import { Text } from "components/ui/source";

import Tooltip from "../Tooltip/Tooltip";

import { BrandSegment, DescriptionType } from "./types";
import { useBrandSegmentConfig } from "./useBrandSegment";

import styles from "./BrandSegmentBadge.module.scss";

export interface BrandSegmentBadgeProps {
  brandSegment: BrandSegment;
  isDescriptionHidden?: boolean;
  style?: CSSProperties;
  className?: string;
  onHover?: () => void;
  descriptionType?: DescriptionType;
}

export const BrandSegmentBadge = ({
  brandSegment,
  isDescriptionHidden,
  style,
  className,
  onHover,
  descriptionType = "singular",
}: BrandSegmentBadgeProps) => {
  const { t } = useTranslation();

  const brandSegmentConfig = useBrandSegmentConfig({
    brandSegment,
    descriptionType,
  });

  if (isEmpty(brandSegmentConfig)) return null;

  const { descriptionTranslationKey, title, backgroundColor } =
    brandSegmentConfig;

  const description = t(descriptionTranslationKey);

  if (isDescriptionHidden)
    return (
      <div className={className} style={style}>
        <Tooltip
          onHover={onHover}
          placement="bottom-start"
          className={className}
          content={description}
          style={style}
        >
          <Badge title={title} backgroundColor={backgroundColor} />
        </Tooltip>
      </div>
    );

  return (
    <div className={classNames(styles.badgeContainer, className)} style={style}>
      <Badge title={title} backgroundColor={backgroundColor} />
      <Description description={description} />
    </div>
  );
};

function Badge({
  backgroundColor,
  title,
}: {
  backgroundColor: string;
  title: string;
}) {
  return (
    <Text
      types={["body1"]}
      className={classNames(styles.badge, {
        [styles[`backgroundColor--${backgroundColor}`]]: !!backgroundColor,
      })}
    >
      {title}
    </Text>
  );
}

function Description({ description }: { description: string }) {
  return (
    <Text types={["body1"]} className={classNames(styles.description)}>
      {description}
    </Text>
  );
}
